import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { KibSectionHeading } from '@chewy/kib-content-groups-react'
import { KibButtonNew, type KibButtonNewProps } from '@chewy/kib-controls-react'
import { KibTextarea } from '@chewy/kib-fields-new-react'
import KibLabel from '@chewy/kib-label-react'
import { KibContainer, KibGrid, KibGridItem } from '@chewy/kib-layout-react'
import { DateUtils, LanguageUtils, Utils } from '@pbt/pbt-ui-components'

import type { InventoryOrder } from '~/api/graphql/generated/types'
import {
  getInventoryOrdersIsUpdating,
  updateInventoryOrderNotes,
} from '~/store/duck/inventoryOrders'
import {
  getInventoryDistributors,
  getInventoryOrderShipmentStatus,
} from '~/store/reducers/constants'

import { transformInventoryOrderForTable } from '../utils'

import styles from './OrderItemsPageHeaderLabels.module.scss'

interface OrderItemsPageHeaderLabelsProps {
  inventoryOrder: InventoryOrder
  receivedBy?: string | null
}

export const OrderItemsPageHeaderLabels = ({
  inventoryOrder: {
    orderNumber,
    orderedDate,
    distributor,
    externalCreatorFirstName,
    externalCreatorLastName,
    status: { id: statusId },
  },
  inventoryOrder,
  receivedBy,
}: OrderItemsPageHeaderLabelsProps) => {
  const { t } = useTranslation(['Admin', 'Common'])
  const dispatch = useDispatch()

  const initialNotes = inventoryOrder.notes || ''
  const [isExpanded, setIsExpanded] = useState(Boolean(initialNotes))
  const [notesValue, setNotesValue] = useState(initialNotes)

  const isSaving = useSelector(getInventoryOrdersIsUpdating)

  const savedNotes = inventoryOrder.notes || ''
  const hasUnsavedChanges = notesValue !== savedNotes

  const InventoryDistributors = useSelector(getInventoryDistributors)
  const distributorConstantName = Utils.getConstantName(
    distributor.id,
    InventoryDistributors,
  )
  const distributorDisplayName = LanguageUtils.getConstantTranslatedName(
    distributor.id,
    InventoryDistributors,
  )

  const InventoryOrderShipmentStatus = useSelector(
    getInventoryOrderShipmentStatus,
  )
  const status = Utils.findById(statusId, InventoryOrderShipmentStatus) || {}
  const statusName = LanguageUtils.getTranslatedFieldName(status)

  const EMPTY_VALUE = '--'
  const externalCreatorName =
    [externalCreatorFirstName, externalCreatorLastName]
      .filter(Boolean)
      .join(' ') || EMPTY_VALUE

  const { lastReceivedDate } = transformInventoryOrderForTable(inventoryOrder)

  const handleToggleNote = () => {
    setIsExpanded(true)
    setNotesValue(savedNotes || '')
  }

  const updateNotes = (notes: string | null) => {
    dispatch(updateInventoryOrderNotes(inventoryOrder.id, notes))
  }

  const handleRemoveNote = () => {
    updateNotes(null)
    setNotesValue('')
    setIsExpanded(false)
  }

  const handleSaveNote = () => {
    if (!hasUnsavedChanges) {
      return
    }

    updateNotes(notesValue || null)
    if (!notesValue) {
      setIsExpanded(false)
    }
  }

  const orderFields = [
    {
      label: t('Admin:CATALOG.ORDERS.ORDER_DATE'),
      value: DateUtils.formatDate(orderedDate),
    },
    {
      label: t('Admin:CATALOG.ORDERS.SUPPLIER'),
      value:
        distributorConstantName === 'Other'
          ? distributor.name
          : distributorDisplayName,
    },
    {
      label: t('Admin:CATALOG.ORDERS.ORDERED_BY'),
      value: externalCreatorName,
    },
    {
      label: t('Admin:CATALOG.ORDERS.RECEIVED_BY'),
      value: receivedBy || EMPTY_VALUE,
    },
    {
      label: t('Admin:CATALOG.ORDERS.LAST_RECEIVED'),
      value: lastReceivedDate
        ? DateUtils.formatDate(lastReceivedDate)
        : EMPTY_VALUE,
    },
  ]

  const noteButtons = isExpanded
    ? [
        {
          label: t('Common:SAVE_NOTE'),
          size: 'large',
          disabled: isSaving || !hasUnsavedChanges,
          onClick: handleSaveNote,
        },
        {
          label: t('Common:REMOVE_NOTE'),
          emphasis: 'tertiary',
          onClick: handleRemoveNote,
        },
      ]
    : [
        {
          label: t('Common:ADD_NOTE'),
          className: styles.linkButton,
          emphasis: 'tertiary',
          onClick: handleToggleNote,
        },
      ]

  return (
    <KibContainer className={styles.kibScope}>
      <header className={styles.orderHeader}>
        <KibSectionHeading
          className={styles.orderNumber}
          heading={t('Admin:CATALOG.ORDERS.ORDER', { orderNumber })}
        />
        <KibLabel className={styles.statusLabel}>{statusName}</KibLabel>
      </header>

      <div className={styles.orderDetailsFrame}>
        <KibGrid>
          {orderFields.map(({ label, value }) => (
            <KibGridItem key={label} push="1@min-xs 1@md 1@min-lg">
              <div>{label}</div>
              <div>{value}</div>
            </KibGridItem>
          ))}
        </KibGrid>
      </div>

      <div className={isExpanded ? styles.notesExpanded : undefined}>
        {isExpanded && (
          <KibTextarea
            label={t('Common:NOTE')}
            value={notesValue}
            onChange={(event) => setNotesValue(event.target.value)}
          />
        )}
        {noteButtons.map(({ label, ...buttonProps }) => (
          <KibButtonNew key={label} {...(buttonProps as KibButtonNewProps)}>
            {label}
          </KibButtonNew>
        ))}
      </div>
    </KibContainer>
  )
}
